import {useMemo, useState} from 'react';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
  Spinner,
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@kroll-resolver-crisp/ui-components-react';
import {useJobItems} from '@/api/hooks';
import {useSetItemFlag} from '@/components/items/useSetItemFlag';
import {ItemsGrid} from '@/components/items/ItemsGrid';
import {getTermMatches, itemSearchText, parseSearchQuery} from '@/lib/searchQuery';
import {cn} from '@/lib/utils';

// No stemming toggle in this view - see the matching constant in ItemsGrid.
const STEM_MATCHES = true;

type Props = {
  readonly jobId: string;
  // The collection's search query - lets the modal explain, per item and per term, why each
  // result came back. Empty for spider types that don't take a free-text query.
  readonly query: string;
  readonly onClose: () => void;
};

export function RunItemsModal({jobId, query, onClose}: Props) {
  const [page, setPage] = useState(1);
  const [isolatedTerm, setIsolatedTerm] = useState<string | null>(null);
  const {data, isLoading, isFetching} = useJobItems(jobId, {page});
  const setItemFlag = useSetItemFlag();
  const items = useMemo(() => data?.results ?? [], [data]);

  const parsedQuery = useMemo(() => parseSearchQuery(query), [query]);
  const allTerms = useMemo(() => [...parsedQuery.include, ...parsedQuery.exclude], [parsedQuery]);

  // Computed once per items/query change and shared by the counts and the isolate filter below,
  // rather than each re-running getTermMatches over every item on every render (a state change
  // like toggling isolatedTerm shouldn't re-stem the same item text it already stemmed).
  const matchesByItem = useMemo(
    () => items.map((item) => getTermMatches(itemSearchText(item), parsedQuery, STEM_MATCHES)),
    [items, parsedQuery]
  );

  // How many of this page's items each term is literally found in - a term at 0 is a candidate to
  // drop from the query, one dominating the count is worth tightening. Computed per page rather
  // than across the whole run: only the loaded items have their text available client-side.
  const termCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const matches of matchesByItem) {
      for (const term of [...matches.matchedInclude, ...matches.matchedExclude]) {
        counts.set(term, (counts.get(term) ?? 0) + 1);
      }
    }

    return counts;
  }, [matchesByItem]);

  const visibleItems = useMemo(
    () =>
      isolatedTerm
        ? items.filter((_, index) => {
            const matches = matchesByItem[index]!;
            return (
              matches.matchedInclude.includes(isolatedTerm) ||
              matches.matchedExclude.includes(isolatedTerm)
            );
          })
        : items,
    [items, matchesByItem, isolatedTerm]
  );

  const paged = !isLoading && (data?.has_more || page > 1);

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}>
      <DialogContent className="sm:max-w-5xl">
        <DialogHeader>
          <DialogTitle>Run results</DialogTitle>
          <DialogDescription>
            Items this run collected, with the query terms each one matched.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="gap-4">
          {isLoading ? (
            <p className="text-muted-foreground flex items-center gap-2 py-12 text-xs">
              <Spinner /> Loading items…
            </p>
          ) : (
            <>
              {allTerms.length > 0 ? (
                <div className="border-border flex flex-wrap items-center gap-1.5 border-b pb-4">
                  <span className="text-muted-foreground text-[10px] tracking-widest uppercase">
                    Query terms{data?.has_more || page > 1 ? ' (this page)' : ''}
                  </span>
                  {allTerms.map((term) => {
                    const negated = parsedQuery.exclude.includes(term);
                    const active = isolatedTerm === term;
                    return (
                      <Tooltip key={term}>
                        <TooltipTrigger
                          render={
                            <Button
                              type="button"
                              size="xs"
                              variant={active ? 'default' : 'secondary'}
                              aria-pressed={active}
                              className={cn(!active && negated && 'text-destructive-ink')}
                              onClick={() => {
                                setIsolatedTerm((previous) => (previous === term ? null : term));
                              }}
                            />
                          }>
                          {negated ? '−' : ''}
                          {term} ({termCounts.get(term) ?? 0})
                        </TooltipTrigger>
                        <TooltipContent>
                          {active
                            ? 'Showing only items matching this term - click to clear'
                            : 'Show only items matching this term'}
                        </TooltipContent>
                      </Tooltip>
                    );
                  })}
                  {isolatedTerm ? (
                    <Button
                      type="button"
                      variant="link"
                      size="xs"
                      onClick={() => {
                        setIsolatedTerm(null);
                      }}>
                      Clear
                    </Button>
                  ) : null}
                </div>
              ) : null}
              <ItemsGrid
                showSearch
                showBlurToggle
                items={visibleItems}
                searchTerm={query}
                onSetFlag={setItemFlag}
              />
            </>
          )}
        </DialogBody>

        {paged ? (
          <DialogFooter sticky className="sm:justify-center">
            {/* No href, so each link renders as a native button and forwards `disabled`; the
                package types the props as an anchor's, so it goes past the type as a spread. */}
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    {...{disabled: page <= 1}}
                    onClick={() => {
                      setPage((p) => p - 1);
                    }}
                  />
                </PaginationItem>
                <PaginationItem>
                  <span className="text-muted-foreground px-2 text-xs tabular-nums">
                    Page {page}
                  </span>
                </PaginationItem>
                <PaginationItem>
                  <PaginationNext
                    {...{disabled: !data?.has_more || isFetching}}
                    onClick={() => {
                      setPage((p) => p + 1);
                    }}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </DialogFooter>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
